/**
 * The de Broglie wavelength, λ = h ÷ p, for any particle, from its speed, kinetic
 * energy, momentum or accelerating voltage, in the exact relativistic form and in the
 * classical approximation p = mv.
 *
 * Masses are CODATA 2022 (NIST); h, c and e are exact SI values.
 */

import {
  ELEMENTARY_CHARGE,
  PLANCK,
  SPEED_OF_LIGHT,
} from "@/lib/tool-kit/physics-waves/electromagnetic";

export type ParticleId =
  | "electron"
  | "proton"
  | "neutron"
  | "alpha"
  | "muon"
  | "custom";

export interface Particle {
  id: Exclude<ParticleId, "custom">;
  /** Mass in kg. */
  mass: number;
  /** Charge in units of the elementary charge. */
  charge: number;
}

export const PARTICLES: readonly Particle[] = [
  { id: "electron", mass: 9.109_383_713_9e-31, charge: 1 },
  { id: "muon", mass: 1.883_531_627e-28, charge: 1 },
  { id: "proton", mass: 1.672_621_925_95e-27, charge: 1 },
  { id: "neutron", mass: 1.674_927_500_56e-27, charge: 0 },
  { id: "alpha", mass: 6.644_657_345e-27, charge: 2 },
];

/** The unified atomic mass unit, kg (CODATA 2022). */
export const ATOMIC_MASS_UNIT = 1.660_539_068_92e-27;

export type Method = "relativistic" | "classical";

export interface Wave {
  /** de Broglie wavelength, m. */
  wavelength: number;
  /** Momentum, kg·m/s. */
  momentum: number;
  /** Kinetic energy, J. */
  kinetic: number;
  /** Speed, m/s. */
  speed: number;
  /** Speed as a fraction of c. */
  beta: number;
  /** Lorentz factor (1 for the classical method). */
  gamma: number;
}

export type WaveError = "invalid" | "too-fast";

export type WaveResult =
  | { ok: true; wave: Wave }
  | { ok: false; error: WaveError };

const C = SPEED_OF_LIGHT;

function build(
  momentum: number,
  kinetic: number,
  speed: number,
  gamma: number
): Wave {
  return {
    wavelength: PLANCK / momentum,
    momentum,
    kinetic,
    speed,
    beta: speed / C,
    gamma,
  };
}

/** From speed: p = γmv (or mv), K = (γ − 1)mc² (or ½mv²). */
export function fromSpeed(
  mass: number,
  speed: number,
  method: Method
): WaveResult {
  if (!(mass > 0 && speed > 0 && Number.isFinite(speed))) {
    return { ok: false, error: "invalid" };
  }
  if (speed >= C) {
    return { ok: false, error: "too-fast" };
  }
  if (method === "classical") {
    return {
      ok: true,
      wave: build(mass * speed, 0.5 * mass * speed * speed, speed, 1),
    };
  }
  const gamma = 1 / Math.sqrt(1 - (speed / C) ** 2);
  return {
    ok: true,
    wave: build(gamma * mass * speed, (gamma - 1) * mass * C * C, speed, gamma),
  };
}

/** From kinetic energy in joules: p = √(2mK) classically, pc = √(K² + 2Kmc²) exactly. */
export function fromKinetic(
  mass: number,
  kinetic: number,
  method: Method
): WaveResult {
  if (!(mass > 0 && kinetic > 0 && Number.isFinite(kinetic))) {
    return { ok: false, error: "invalid" };
  }
  if (method === "classical") {
    const momentum = Math.sqrt(2 * mass * kinetic);
    const speed = momentum / mass;
    return speed >= C
      ? { ok: false, error: "too-fast" }
      : { ok: true, wave: build(momentum, kinetic, speed, 1) };
  }
  const rest = mass * C * C;
  const momentum = Math.sqrt(kinetic * kinetic + 2 * kinetic * rest) / C;
  const total = kinetic + rest;
  return {
    ok: true,
    wave: build(momentum, kinetic, (momentum * C * C) / total, total / rest),
  };
}

/** From momentum: v = p ÷ m classically, v = pc² ÷ E exactly. */
export function fromMomentum(
  mass: number,
  momentum: number,
  method: Method
): WaveResult {
  if (!(mass > 0 && momentum > 0 && Number.isFinite(momentum))) {
    return { ok: false, error: "invalid" };
  }
  if (method === "classical") {
    const speed = momentum / mass;
    return speed >= C
      ? { ok: false, error: "too-fast" }
      : {
          ok: true,
          wave: build(momentum, (momentum * momentum) / (2 * mass), speed, 1),
        };
  }
  const rest = mass * C * C;
  const total = Math.sqrt((momentum * C) ** 2 + rest * rest);
  return {
    ok: true,
    wave: build(
      momentum,
      total - rest,
      (momentum * C * C) / total,
      total / rest
    ),
  };
}

/** A charge of `chargeNumber` × e accelerated through `volts` gains K = qV. */
export function fromVoltage(
  mass: number,
  chargeNumber: number,
  volts: number,
  method: Method
): WaveResult {
  return fromKinetic(mass, chargeNumber * ELEMENTARY_CHARGE * volts, method);
}

/**
 * How far the classical wavelength is from the exact one, in percent of the exact value:
 * positive when the classical formula gives the longer wavelength.
 */
export function classicalError(exact: Wave, classical: Wave): number {
  return ((classical.wavelength - exact.wavelength) / exact.wavelength) * 100;
}
